import * as React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { ApiKeyManager } from './ApiKeyManager';
import { TagManager } from './TagManager';
import { Page } from '@/components/layout/Page';
import { authApi } from '@/lib/api';
import { clearAllLocalData } from '@/lib/db';
import { buildUserDataExport, downloadJsonExport } from '@/lib/exportUserData';
import { NotificationsSettingsCard } from '@/components/push/NotificationsSettingsCard';
import { SecuritySettingsCard } from '@/components/security/SecuritySettingsCard';
import { toast } from 'sonner';
import { clearLegacyAccessTokenStorage } from '@/lib/api';

function formatProvider(provider?: string): string {
  if (!provider || provider === 'cognito') return 'Email';
  return provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
}

const NAME_PATTERN = /^[a-zA-Z0-9_]+$/;

export function SettingsPage() {
  const { user, logout, refreshUser, requireElevatedAuth } = useAuth();

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const [isSavingName, setIsSavingName] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [apiKeysUnlocked, setApiKeysUnlocked] = React.useState(false);
  const DELETE_CONFIRM_WORD = 'DELETE';

  React.useEffect(() => {
    setNameInput(user?.name ?? '');
  }, [user?.name]);

  const ensureElevated = async (): Promise<boolean> => {
    if (!navigator.onLine) {
      toast.error('You must be online to change security settings.');
      return false;
    }
    const result = await requireElevatedAuth();
    if (!result.ok) {
      toast.error(result.message || 'Verification required');
      return false;
    }
    return true;
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== DELETE_CONFIRM_WORD) return;
    if (!(await ensureElevated())) return;
    setIsDeleting(true);
    try {
      await authApi.deleteAccount();
      await clearAllLocalData();
      clearLegacyAccessTokenStorage();
      toast.success('Account deleted. A confirmation email will be sent if mail is configured.');
      window.location.href = '/';
    } catch {
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    if (!NAME_PATTERN.test(trimmed)) {
      toast.error('Name can only contain letters, numbers, and underscores');
      return;
    }
    if (trimmed === user?.name) return;

    setIsSavingName(true);
    try {
      await authApi.updateName(trimmed);
      await refreshUser();
      toast.success('Name updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update name';
      toast.error(message);
    } finally {
      setIsSavingName(false);
    }
  };

  const nameChanged = nameInput.trim() !== (user?.name ?? '');
  const nameValid = nameInput.trim().length > 0 && NAME_PATTERN.test(nameInput.trim());

  const handleExportData = async () => {
    if (!user?.id) {
      toast.error('You must be signed in to export data');
      return;
    }
    setIsExporting(true);
    try {
      const data = await buildUserDataExport(user.id);
      downloadJsonExport(data);
      toast.success('Export downloaded');
    } catch {
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Page className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage your account and preferences
        </p>
      </div>

      {/* Account section */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {user && (
            <div className="space-y-3">
              <div className="space-y-2">
                <span className="text-sm text-muted-foreground">Name</span>
                <div className="flex gap-2">
                  <Input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value.replace(/\s/g, ''))}
                    placeholder="your_name"
                    autoComplete="off"
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSaveName}
                    loading={isSavingName}
                    disabled={!nameChanged || !nameValid}
                  >
                    Save
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Letters, numbers, and underscores only. Others can invite you to shared budgets using this name.
                </p>
              </div>
              {user.displayName && user.displayName !== user.name && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Display name</span>
                  <span className="text-sm font-medium text-right">{user.displayName}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-muted-foreground">Username</span>
                <span className="text-sm font-medium text-right break-all">{user.username}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium text-right break-all">
                  {user.email || '—'}
                </span>
              </div>
              {user.provider && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Signed in with</span>
                  <span className="text-sm font-medium">{formatProvider(user.provider)}</span>
                </div>
              )}
              {user.providerManaged && (
                <p className="text-xs text-muted-foreground">
                  Email and display name are managed by your sign-in provider. Your My Budget name can still be changed above.
                </p>
              )}
            </div>
          )}
          <Button variant="outline" onClick={logout} className="w-full">
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Notifications — always visible; Enable only in installed PWA when supported */}
      <NotificationsSettingsCard />

      {/* Face ID / passkeys — installed PWA + credential management */}
      <SecuritySettingsCard />

      {/* Data & sync */}
      <Card>
        <CardHeader>
          <CardTitle>Your data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Download a JSON copy of your budgets and transactions anytime. Keep it as a backup
            or move it elsewhere.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleExportData}
            loading={isExporting}
          >
            Export my data
          </Button>
        </CardContent>
      </Card>

      {/* Automation */}
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!navigator.onLine ? (
            <p className="text-sm text-muted-foreground">
              You must be online to change security settings.
            </p>
          ) : apiKeysUnlocked ? (
            <ApiKeyManager />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Verify your identity to manage API keys.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  if (await ensureElevated()) setApiKeysUnlocked(true);
                }}
              >
                Unlock API keys
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TagManager />
        </CardContent>
      </Card>

      {/* Administration */}
      {user?.isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Administration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Configure application-wide settings such as SMTP mail server.
            </p>
            <Link to="/admin">
              <Button variant="outline" className="w-full">
                Global Settings
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* About */}
      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Version</span>
            <span className="text-sm font-medium">{__APP_VERSION__}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Commit</span>
            <span className="text-sm font-medium font-mono">{__APP_COMMIT__}</span>
          </div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Permanently delete your account and all associated data. This action cannot be undone.
            A confirmation email will be sent to your address.
          </p>
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete Account
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteConfirmText('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              This will permanently delete your account, budgets, transactions, and all other data.
              This action cannot be undone. A confirmation email will be sent to{' '}
              <strong>{user?.email}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Type <strong>{DELETE_CONFIRM_WORD}</strong> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={DELETE_CONFIRM_WORD}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              loading={isDeleting}
              disabled={deleteConfirmText !== DELETE_CONFIRM_WORD}
            >
              Delete my account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
